import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaBuilding, FaIndustry, FaUsers, FaGlobe, FaPhone, FaUser, FaCheck } from 'react-icons/fa';

const OrganizationSetup = () => {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    size: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    website: '',
    phone: '',
    primaryContact: {
      name: '',
      email: '',
      phone: ''
    }
  });
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.industry) {
      setError('Organization name and industry are required');
      return;
    }
    
    try {
      setLoading(true);
      console.log('Submitting organization data:', formData);
      
      // Create organization
      const response = await axios.post('/api/organizations', formData);
      console.log('Organization created successfully:', response.data);
      
      setLoading(false);
      
      // Redirect to dashboard
      navigate('/');
    } catch (err) {
      console.error('Error creating organization:', err);
      setError(err.response?.data?.msg || 'Failed to create organization');
      setLoading(false);
      // If there's an error, don't reset the step
      return;
    }
  };
  
  // Styles
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '20px',
      backgroundColor: '#FFFAFA'
    },
    card: {
      backgroundColor: '#FFFAFA',
      borderRadius: '8px',
      boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
      width: '100%',
      maxWidth: '800px',
      overflow: 'hidden'
    },
    header: {
      backgroundColor: '#FFFAFA',
      color: '#243444',
      padding: '20px',
      textAlign: 'center',
      borderBottom: '1px solid #eee'
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      margin: 0,
      color: '#243444'
    },
    subtitle: {
      fontSize: '16px',
      marginTop: '10px',
      color: '#4C5454'
    },
    content: {
      padding: '30px'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '5px'
    },
    label: {
      fontWeight: 'bold',
      color: '#243444',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    input: {
      padding: '10px',
      borderRadius: '4px',
      border: '1px solid #ccc',
      fontSize: '16px'
    },
    select: {
      padding: '10px',
      borderRadius: '4px',
      border: '1px solid #ccc',
      fontSize: '16px'
    },
    error: {
      color: '#7C1227',
      marginBottom: '15px',
      textAlign: 'center'
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: '20px'
    },
    button: {
      padding: '12px 20px',
      borderRadius: '4px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    primaryButton: {
      backgroundColor: '#243444',
      color: '#D2BA7B'
    },
    secondaryButton: {
      backgroundColor: 'transparent',
      border: '1px solid #243444',
      color: '#243444'
    },
    stepIndicator: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '30px'
    },
    step: {
      width: '30px',
      height: '30px',
      borderRadius: '50%',
      backgroundColor: '#eee',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 5px',
      color: '#4C5454'
    },
    activeStep: {
      backgroundColor: '#243444',
      color: '#D2BA7B'
    },
    stepConnector: {
      height: '2px',
      width: '30px',
      backgroundColor: '#eee',
      alignSelf: 'center'
    },
    activeStepConnector: {
      backgroundColor: '#243444'
    },
    logo: {
      width: '400px',
      marginBottom: '30px',
      maxWidth: '90%'
    },
    addressGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '15px'
    }
  };
  
  // Render step 1: Basic information
  const renderStep1 = () => (
    <div>
      <div style={styles.formGroup}>
        <label style={styles.label} htmlFor="name">
          <FaBuilding /> Organization Name*
        </label>
        <input
          type="text"
          id="name"
          name="name"
          style={styles.input}
          value={formData.name}
          onChange={handleChange}
          placeholder="Enter organization name"
          required
        />
      </div>
      
      <div style={styles.formGroup}>
        <label style={styles.label} htmlFor="industry">
          <FaIndustry /> Industry*
        </label>
        <select
          id="industry"
          name="industry"
          style={styles.select}
          value={formData.industry}
          onChange={handleChange}
          required
        >
          <option value="">Select industry</option>
          <option value="Aerospace & Defense">Aerospace & Defense</option>
          <option value="Agriculture">Agriculture</option>
          <option value="Automotive">Automotive</option>
          <option value="Banking">Banking</option>
          <option value="Biotechnology">Biotechnology</option>
          <option value="Chemicals">Chemicals</option>
          <option value="College">College & University</option>
          <option value="Construction">Construction</option>
          <option value="Consulting">Consulting</option>
          <option value="Consumer Goods">Consumer Goods</option>
          <option value="Education">Education (K-12)</option>
          <option value="Energy">Energy & Utilities</option>
          <option value="Entertainment">Entertainment & Media</option>
          <option value="Financial">Financial Services</option>
          <option value="Food & Beverage">Food & Beverage</option>
          <option value="Government">Government</option>
          <option value="Healthcare">Healthcare</option>
          <option value="Hospitality">Hospitality & Tourism</option>
          <option value="Information Technology">Information Technology</option>
          <option value="Insurance">Insurance</option>
          <option value="Legal">Legal Services</option>
          <option value="Manufacturing">Manufacturing</option>
          <option value="Mining">Mining & Metals</option>
          <option value="Non-Profit">Non-Profit</option>
          <option value="Pharmaceuticals">Pharmaceuticals</option>
          <option value="Real Estate">Real Estate</option>
          <option value="Retail">Retail</option>
          <option value="Telecommunications">Telecommunications</option>
          <option value="Transportation">Transportation & Logistics</option>
          <option value="Other">Other</option>
        </select>
      </div>
      
      <div style={styles.formGroup}>
        <label style={styles.label} htmlFor="size">
          <FaUsers /> Organization Size
        </label>
        <select
          id="size"
          name="size"
          style={styles.select}
          value={formData.size}
          onChange={handleChange}
        >
          <option value="">Select size</option>
          <option value="Small (1-50)">Small (1-50 employees)</option>
          <option value="Medium (51-500)">Medium (51-500 employees)</option>
          <option value="Large (501-5000)">Large (501-5000 employees)</option>
          <option value="Enterprise (5000+)">Enterprise (5000+ employees)</option>
        </select>
      </div>
    </div>
  );
  
  // Render step 2: Contact information
  const renderStep2 = () => (
    <div>
      <div style={styles.formGroup}>
        <label style={styles.label} htmlFor="website">
          <FaGlobe /> Website
        </label>
        <input
          type="url"
          id="website"
          name="website"
          style={styles.input}
          value={formData.website}
          onChange={handleChange}
          placeholder="https://example.com"
        />
      </div>
      
      <div style={styles.formGroup}>
        <label style={styles.label} htmlFor="phone">
          <FaPhone /> Phone Number
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          style={styles.input}
          value={formData.phone}
          onChange={handleChange}
          placeholder="(123) 456-7890"
        />
      </div>
      
      <div style={styles.formGroup}>
        <label style={styles.label}>Address</label>
        <div style={styles.addressGrid}>
          <input
            type="text"
            name="address.street"
            style={styles.input}
            value={formData.address.street}
            onChange={handleChange}
            placeholder="Street"
          />
          <input
            type="text"
            name="address.city"
            style={styles.input}
            value={formData.address.city}
            onChange={handleChange}
            placeholder="City"
          />
          <input
            type="text"
            name="address.state"
            style={styles.input}
            value={formData.address.state}
            onChange={handleChange}
            placeholder="State/Province"
          />
          <input
            type="text"
            name="address.zipCode"
            style={styles.input}
            value={formData.address.zipCode}
            onChange={handleChange}
            placeholder="Zip/Postal Code"
          />
          <input
            type="text"
            name="address.country"
            style={styles.input}
            value={formData.address.country}
            onChange={handleChange}
            placeholder="Country"
          />
        </div>
      </div>
    </div>
  );
  
  // Render step 3: Primary contact
  const renderStep3 = () => (
    <div>
      <div style={styles.formGroup}>
        <label style={styles.label} htmlFor="primaryContact.name">
          <FaUser /> Primary Contact Name
        </label>
        <input
          type="text"
          id="primaryContact.name"
          name="primaryContact.name"
          style={styles.input}
          value={formData.primaryContact.name}
          onChange={handleChange}
          placeholder="Enter contact name"
        />
      </div>
      
      <div style={styles.formGroup}>
        <label style={styles.label} htmlFor="primaryContact.email">
          <FaUser /> Primary Contact Email
        </label>
        <input
          type="email"
          id="primaryContact.email"
          name="primaryContact.email"
          style={styles.input}
          value={formData.primaryContact.email}
          onChange={handleChange}
          placeholder="Enter contact email"
        />
      </div>
      
      <div style={styles.formGroup}>
        <label style={styles.label} htmlFor="primaryContact.phone">
          <FaPhone /> Primary Contact Phone
        </label>
        <input
          type="tel"
          id="primaryContact.phone"
          name="primaryContact.phone"
          style={styles.input}
          value={formData.primaryContact.phone}
          onChange={handleChange}
          placeholder="Enter contact phone"
        />
      </div>
    </div>
  );
  
  // Render navigation buttons
  const renderNavButtons = () => (
    <div style={styles.buttonGroup}>
      {step > 1 && (
        <button
          type="button"
          style={{ ...styles.button, ...styles.secondaryButton }}
          onClick={() => setStep(step - 1)}
        >
          Back
        </button>
      )}
      
      {step < 3 ? (
        <button
          type="button"
          style={{ ...styles.button, ...styles.primaryButton, marginLeft: step > 1 ? '0' : 'auto' }}
          onClick={(e) => {
            e.preventDefault(); // Prevent form submission
            setStep(step + 1);
          }}
        >
          Next
        </button>
      ) : (
        <button
          type="submit"
          style={{ ...styles.button, ...styles.primaryButton, marginLeft: 'auto' }}
          disabled={loading}
        >
          <FaCheck /> {loading ? 'Creating...' : 'Complete Setup'}
        </button>
      )}
    </div>
  );
  
  // Render step indicator
  const renderStepIndicator = () => (
    <div style={styles.stepIndicator}>
      <div style={{ ...styles.step, ...(step >= 1 ? styles.activeStep : {}) }}>1</div>
      <div style={{ ...styles.stepConnector, ...(step >= 2 ? styles.activeStepConnector : {}) }}></div>
      <div style={{ ...styles.step, ...(step >= 2 ? styles.activeStep : {}) }}>2</div>
      <div style={{ ...styles.stepConnector, ...(step >= 3 ? styles.activeStepConnector : {}) }}></div>
      <div style={{ ...styles.step, ...(step >= 3 ? styles.activeStep : {}) }}>3</div>
    </div>
  );
  
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <img 
            src="http://localhost:5001/images/Navigator Logo Transparent.png" 
            alt="Business Resilience Navigator" 
            style={styles.logo} 
            width="400"
          />
          <h1 style={styles.title}>Organization Setup</h1>
          <p style={styles.subtitle}>Set up your organization to get started with Business Resilience Navigator</p>
        </div>
        
        <div style={styles.content}>
          {error && <div style={styles.error}>{error}</div>}
          
          {renderStepIndicator()}
          
          <form style={styles.form} onSubmit={(e) => {
            // Only submit the form when on the last step
            if (step < 3) {
              e.preventDefault();
              return false;
            }
            handleSubmit(e);
          }}>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            
            {renderNavButtons()}
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrganizationSetup;
